import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setPageContent } from '@/lib/api/content';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { getStore, refreshStore } from '@/lib/store';

interface PageField {
  key: string;
  label: string;
  labelKey: string;
  type: 'text' | 'textarea' | 'richtext';
  placeholder: string;
  placeholderKey: string;
}

interface PageConfig {
  slug: string;
  label: string;
  labelKey: string;
  icon: string;
  fields: PageField[];
}

const LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'sq', label: 'Shqip', flag: '🇦🇱' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const PAGES: PageConfig[] = [
  {
    slug: 'home', label: 'Startseite', labelKey: 'adm2.pc_page_home', icon: 'ri-home-4-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Ihre Premium-Klinik in Tirana', placeholderKey: 'adm2.pc_ph_home_hero_title' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Hochwertige Behandlungen zu fairen Preisen...', placeholderKey: 'adm2.pc_ph_home_hero_sub' },
      { key: 'hero_cta', label: 'Hero-Button', labelKey: 'adm2.pc_hero_button', type: 'text', placeholder: 'z.B. Jetzt Termin buchen', placeholderKey: 'adm2.pc_ph_home_hero_cta' },
      { key: 'about_title', label: 'Über-uns-Überschrift', labelKey: 'adm2.pc_about_heading', type: 'text', placeholder: 'z.B. Über Esteva Clinic', placeholderKey: 'adm2.pc_ph_about_title' },
      { key: 'about_text', label: 'Über-uns-Text', labelKey: 'adm2.pc_about_text', type: 'textarea', placeholder: 'Beschreibung der Klinik...', placeholderKey: 'adm2.pc_ph_clinic_desc' },
      { key: 'services_title', label: 'Leistungen-Überschrift', labelKey: 'adm2.pc_services_heading', type: 'text', placeholder: 'z.B. Unsere Leistungen', placeholderKey: 'adm2.pc_ph_our_services' },
      { key: 'pricing_title', label: 'Preise-Überschrift', labelKey: 'adm2.pc_pricing_heading', type: 'text', placeholder: 'z.B. Unsere Preispakete', placeholderKey: 'adm2.pc_ph_pricing_packages' },
      { key: 'testimonials_title', label: 'Bewertungen-Überschrift', labelKey: 'adm2.pc_testimonials_heading', type: 'text', placeholder: 'z.B. Das sagen unsere Kunden', placeholderKey: 'adm2.pc_ph_testimonials' },
    ],
  },
  {
    slug: 'services', label: 'Leistungen', labelKey: 'adm2.pc_page_services', icon: 'ri-service-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Unsere Leistungen', placeholderKey: 'adm2.pc_ph_our_services' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Entdecken Sie unser vollständiges Behandlungsspektrum...', placeholderKey: 'adm2.pc_ph_services_sub' },
      { key: 'hair_title', label: 'Haartransplantation Titel', labelKey: 'adm2.pc_hair_title', type: 'text', placeholder: 'z.B. Haartransplantation', placeholderKey: 'adm2.pc_ph_hair' },
      { key: 'hair_text', label: 'Haartransplantation Beschreibung', labelKey: 'adm2.pc_hair_desc', type: 'textarea', placeholder: 'Beschreibung...', placeholderKey: 'adm2.pc_ph_description' },
      { key: 'dental_title', label: 'Dental-Kosmetik Titel', labelKey: 'adm2.pc_dental_title', type: 'text', placeholder: 'z.B. Dental-Kosmetik', placeholderKey: 'adm2.pc_ph_dental' },
      { key: 'dental_text', label: 'Dental-Kosmetik Beschreibung', labelKey: 'adm2.pc_dental_desc', type: 'textarea', placeholder: 'Beschreibung...', placeholderKey: 'adm2.pc_ph_description' },
      { key: 'botox_title', label: 'Botox & Filler Titel', labelKey: 'adm2.pc_botox_title', type: 'text', placeholder: 'z.B. Botox & Filler', placeholderKey: 'adm2.pc_ph_botox' },
      { key: 'botox_text', label: 'Botox & Filler Beschreibung', labelKey: 'adm2.pc_botox_desc', type: 'textarea', placeholder: 'Beschreibung...', placeholderKey: 'adm2.pc_ph_description' },
    ],
  },
  {
    slug: 'about', label: 'Über uns', labelKey: 'adm2.pc_page_about', icon: 'ri-building-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Über Esteva Clinic', placeholderKey: 'adm2.pc_ph_about_title' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Ihre Premium-Klinik im Herzen von Tirana...', placeholderKey: 'adm2.pc_ph_about_sub' },
      { key: 'story_title', label: 'Unsere Geschichte Titel', labelKey: 'adm2.pc_story_title', type: 'text', placeholder: 'z.B. Unsere Geschichte', placeholderKey: 'adm2.pc_ph_our_story' },
      { key: 'story_text', label: 'Unsere Geschichte Text', labelKey: 'adm2.pc_story_text', type: 'textarea', placeholder: 'Die Geschichte der Klinik...', placeholderKey: 'adm2.pc_ph_clinic_story' },
      { key: 'philosophy_title', label: 'Philosophie Titel', labelKey: 'adm2.pc_philosophy_title', type: 'text', placeholder: 'z.B. Unsere Philosophie', placeholderKey: 'adm2.pc_ph_our_philosophy' },
      { key: 'philosophy_text', label: 'Philosophie Text', labelKey: 'adm2.pc_philosophy_text', type: 'textarea', placeholder: 'Beschreibung...', placeholderKey: 'adm2.pc_ph_description' },
    ],
  },
  {
    slug: 'pricing', label: 'Preise', labelKey: 'adm2.pc_page_pricing', icon: 'ri-money-euro-circle-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Unsere Preise', placeholderKey: 'adm2.pc_ph_our_prices' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Transparente Preise — bis zu 70% günstiger...', placeholderKey: 'adm2.pc_ph_pricing_sub' },
      { key: 'hair_title', label: 'Haartransplantation Überschrift', labelKey: 'adm2.pc_hair_heading', type: 'text', placeholder: 'z.B. Haartransplantation', placeholderKey: 'adm2.pc_ph_hair' },
      { key: 'dental_title', label: 'Dental-Kosmetik Überschrift', labelKey: 'adm2.pc_dental_heading', type: 'text', placeholder: 'z.B. Dental-Kosmetik', placeholderKey: 'adm2.pc_ph_dental' },
      { key: 'botox_title', label: 'Botox & Filler Überschrift', labelKey: 'adm2.pc_botox_heading', type: 'text', placeholder: 'z.B. Botox & Filler', placeholderKey: 'adm2.pc_ph_botox' },
    ],
  },
  {
    slug: 'gallery', label: 'Galerie', labelKey: 'adm2.pc_page_gallery', icon: 'ri-image-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Vorher-Nachher-Galerie', placeholderKey: 'adm2.pc_ph_gallery_title' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Echte Ergebnisse unserer Behandlungen...', placeholderKey: 'adm2.pc_ph_gallery_sub' },
    ],
  },
  {
    slug: 'team', label: 'Team', labelKey: 'adm2.pc_page_team', icon: 'ri-team-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Unser Team', placeholderKey: 'adm2.pc_ph_our_team' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Lernen Sie unsere Experten kennen...', placeholderKey: 'adm2.pc_ph_team_sub' },
    ],
  },
  {
    slug: 'booking', label: 'Terminbuchung', labelKey: 'adm2.pc_page_booking', icon: 'ri-calendar-check-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Termin buchen', placeholderKey: 'adm2.pc_ph_book_appt' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. Vereinbaren Sie jetzt Ihren Beratungstermin...', placeholderKey: 'adm2.pc_ph_booking_sub' },
      { key: 'form_title', label: 'Formular-Überschrift', labelKey: 'adm2.pc_form_heading', type: 'text', placeholder: 'z.B. Ihre Daten', placeholderKey: 'adm2.pc_ph_your_data' },
    ],
  },
  {
    slug: 'contact', label: 'Kontakt', labelKey: 'adm2.pc_page_contact', icon: 'ri-contacts-line',
    fields: [
      { key: 'hero_title', label: 'Hero-Headline', labelKey: 'adm2.pc_hero_headline', type: 'text', placeholder: 'z.B. Kontakt', placeholderKey: 'adm2.pc_ph_contact' },
      { key: 'hero_subtitle', label: 'Hero-Subline', labelKey: 'adm2.pc_hero_subline', type: 'textarea', placeholder: 'z.B. So erreichen Sie uns...', placeholderKey: 'adm2.pc_ph_contact_sub' },
    ],
  },
];

export default function PagesContentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(PAGES[0].slug);
  const [activeLang, setActiveLang] = useState('de');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadContent();
  }, [navigate]);

  // Text content is stored in page_content under keys namespaced "text.<field>"
  // so it doesn't collide with image keys ("images.*").
  const loadContent = () => {
    const content = getStore().content;
    const data: Record<string, string> = {};
    PAGES.forEach((page) => {
      LANGUAGES.forEach((lang) => {
        const map = content[`${page.slug}_${lang.code}`] || {};
        page.fields.forEach((field) => {
          const val = map[`text.${field.key}`];
          if (val) data[`${page.slug}_${lang.code}_${field.key}`] = val;
        });
      });
    });
    setFormData(data);
  };

  const getValue = (pageSlug: string, langCode: string, fieldKey: string): string => {
    const dataKey = `${pageSlug}_${langCode}_${fieldKey}`;
    return formData[dataKey] || '';
  };

  const setValue = (pageSlug: string, langCode: string, fieldKey: string, value: string) => {
    const dataKey = `${pageSlug}_${langCode}_${fieldKey}`;
    setFormData((prev) => ({ ...prev, [dataKey]: value }));
  };

  const handleSave = async () => {
    // Group entries by page+language into { "text.<field>": value }
    const grouped: Record<string, Record<string, string>> = {};
    Object.entries(formData).forEach(([key, value]) => {
      const parts = key.split('_');
      const pageSlug = parts[0];
      const langCode = parts[1];
      const fieldKey = parts.slice(2).join('_');
      const groupKey = `${pageSlug}__${langCode}`;
      if (!grouped[groupKey]) grouped[groupKey] = {};
      grouped[groupKey][`text.${fieldKey}`] = value;
    });

    try {
      await Promise.all(
        Object.entries(grouped).map(([groupKey, fields]) => {
          const [pageSlug, langCode] = groupKey.split('__');
          return setPageContent(pageSlug, langCode, fields);
        }),
      );
      await logActivityDb('page_edited', `${PAGES.find((p) => p.slug === activePage)?.label} (${activeLang.toUpperCase()}) bearbeitet`);
      await refreshStore();
      setSavedMsg(t('adm2.saved'));
    } catch {
      setSavedMsg(t('adm2.save_error'));
    }
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const currentPage = PAGES.find((p) => p.slug === activePage);

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-900">{t('adm2.pages_content_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">{t('adm2.pages_content_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && (
            <span className="text-xs text-green-600 font-label">{savedMsg}</span>
          )}
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm2.save')}
          </button>
        </div>
      </div>

      {/* ── Page Tabs ─────────────────────────────── */}
      <div className="flex items-center gap-1 flex-wrap bg-background-50 rounded-xl border border-background-200/70 p-1">
        {PAGES.map((page) => (
          <button
            key={page.slug}
            onClick={() => setActivePage(page.slug)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              activePage === page.slug
                ? 'bg-primary-500 text-white'
                : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${page.icon} text-xs`}></i>
            </div>
            {t(page.labelKey)}
          </button>
        ))}
      </div>

      {/* ── Language Tabs ──────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400 mr-1">{t('adm2.language_label')}</span>
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setActiveLang(lang.code)}
            className={`px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              activeLang === lang.code
                ? 'bg-foreground-900 text-white'
                : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {lang.flag} {lang.label}
          </button>
        ))}
      </div>

      {/* ── Editable Fields ────────────────────────── */}
      {currentPage && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${currentPage.icon} text-sm`}></i>
            </div>
            <div>
              <h2 className="font-label text-sm font-semibold text-foreground-900">{t(currentPage.labelKey)}</h2>
              <p className="text-[10px] text-foreground-400 font-body">{t('adm2.editable_fields_count', { count: currentPage.fields.length })}</p>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {currentPage.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
                  {t(field.labelKey)}
                  <span className="ml-1 text-[10px] text-foreground-400 font-normal">
                    ({LANGUAGES.find((l) => l.code === activeLang)?.label})
                  </span>
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={getValue(activePage, activeLang, field.key)}
                    onChange={(e) => setValue(activePage, activeLang, field.key, e.target.value)}
                    placeholder={t(field.placeholderKey)}
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all resize-y"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue(activePage, activeLang, field.key)}
                    onChange={(e) => setValue(activePage, activeLang, field.key, e.target.value)}
                    placeholder={t(field.placeholderKey)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                  />
                )}
              </div>
            ))}

            {currentPage.fields.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-file-text-line text-3xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-400 font-body">{t('adm2.no_fields_configured')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}